import React, { useState, useCallback, useEffect } from 'react';
import { Container, Paper, Toolbar, Tabs, Tab, Box, TextField, Divider } from '@mui/material';
import { useLocation, Link, Routes, Route, Outlet } from 'react-router-dom';
import { debounce } from 'lodash';

import { userSelector } from 'helper/security';
import i18next from 'i18next';
import ClientsList from './clients';
import { useQuery } from '@apollo/client';
import CompaniesList from './companies';
import { BOOKINGS_LIST_SEARCH_QUERY, HOTELS_LIST_SEARCH_QUERY, PUBLIC_CLIENT_LIST_SEARCH_QUERY, PUBLIC_COMPANIES_LIST_SEARCH_QUERY } from './gql';
import { HL_PublicClientOutput, HL_PublicCompanyOutput, HLBookingListOutput, HotelListOutput } from '__generated__/graphql';
import { Search as SearchIcon } from '@mui/icons-material';
import BookingsList from './bookings';
import HotelsList from './hotels';

const MIN_SEARCH_LENGTH = 3;

export default function SearchIndex() {
  const location = useLocation();
  const user = userSelector()!;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const debouncedUpdate = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedUpdate(searchTerm);
  }, [searchTerm, debouncedUpdate]);

  const searchQuery = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH ? debouncedSearchTerm : '';
  const showValidationError = searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH;

  const {
    data: dataClients,
    loading: loadingClients,
    error: errorClients,
  } = useQuery(PUBLIC_CLIENT_LIST_SEARCH_QUERY, {
    variables: { query: searchQuery },
  });

  const {
    data: dataCompanies,
    loading: loadingCompanies,
    error: errorCompanies,
  } = useQuery(PUBLIC_COMPANIES_LIST_SEARCH_QUERY, {
    variables: { query: searchQuery },
  });

  const {
    data: dataBookings,
    loading: loadingBookings,
    error: errorBookings,
  } = useQuery(BOOKINGS_LIST_SEARCH_QUERY, {
    variables: { query: searchQuery },
  });

  const {
    data: dataHotels,
    loading: loadingHotels,
    error: errorHotels,
  } = useQuery(HOTELS_LIST_SEARCH_QUERY, {
    variables: { query: searchQuery },
  });

  return (
    <>
      <Toolbar />
      <Container maxWidth="xl">
        <Paper>
          <Box p={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon />
              <TextField
                label={i18next.t('search')}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                error={showValidationError}
                helperText={showValidationError ? i18next.t('search-min-length-error', { count: MIN_SEARCH_LENGTH }) : undefined}
                sx={{
                  minWidth: 300,
                  '& .MuiInputBase-input': { py: 0.5 },
                }}
              />
            </Box>
          </Box>
          <Divider />
          <Tabs value={location.pathname === '/search' ? '/search/companies' : location.pathname.split('/').slice(0, 3).join('/')}>
            <Tab
              value="/search/companies"
              label={`${i18next.t('search-tabs-header-companies')} (${dataCompanies ? dataCompanies.searchPublicCompanies.length : '?'})`}
              component={Link}
              to="/search/companies"
            />
            <Tab
              value="/search/clients"
              label={`${i18next.t('search-tabs-header-clients')} (${dataClients ? dataClients.searchPublicClients.length : '?'})`}
              component={Link}
              to="/search/clients"
            />
            <Tab
              value="/search/bookings"
              label={`${i18next.t('search-tabs-header-bookings')} (${dataBookings ? dataBookings.searchBookings.length : '?'})`}
              component={Link}
              to="/search/bookings"
            />
            <Tab
              value="/search/hotels"
              label={`${i18next.t('search-tabs-header-hotels')} (${dataHotels ? dataHotels.searchHotels.length : '?'})`}
              component={Link}
              to="/search/hotels"
            />
          </Tabs>

          <Box p={2}>
            <Routes>
              <Route index element={<CompaniesList companies={(dataCompanies?.searchPublicCompanies as HL_PublicCompanyOutput[]) || []} />} />
              <Route path="bookings" element={<BookingsList bookings={(dataBookings?.searchBookings as HLBookingListOutput[]) || []} />} />
              <Route path="clients" element={<ClientsList clients={(dataClients?.searchPublicClients as HL_PublicClientOutput[]) || []} />} />
              <Route
                path="companies"
                element={<CompaniesList companies={(dataCompanies?.searchPublicCompanies as HL_PublicCompanyOutput[]) || []} />}
              />
              <Route path="hotels" element={<HotelsList hotels={(dataHotels?.searchHotels as HotelListOutput[]) || []} />} />
            </Routes>

            <Outlet />
          </Box>
        </Paper>
      </Container>
    </>
  );
}
